import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { submitSurvey, fetchSurvey } from '../services/api';
import {
  ClipboardList, ArrowRight, ArrowLeft, CheckCircle2,
  Activity, Moon, Utensils, Brain, Heart, Dumbbell
} from 'lucide-react';

const STEPS = [
  { key: 'basics', title: 'Basic Information', icon: Activity, description: 'Tell us about yourself' },
  { key: 'exercise', title: 'Exercise & Activity', icon: Dumbbell, description: 'Your physical activity level' },
  { key: 'sleep', title: 'Sleep & Recovery', icon: Moon, description: 'Your sleep patterns' },
  { key: 'nutrition', title: 'Nutrition & Diet', icon: Utensils, description: 'Your dietary habits' },
  { key: 'wellness', title: 'Mental Wellness', icon: Brain, description: 'Stress and mental health' },
  { key: 'medical', title: 'Medical History', icon: Heart, description: 'Health conditions & family history' },
];

const CHRONIC_OPTIONS = ['Diabetes', 'Hypertension', 'Heart Disease', 'Asthma', 'Arthritis', 'Cancer', 'Thyroid', 'None'];
const FAMILY_OPTIONS = ['Heart Disease', 'Diabetes', 'Cancer', 'Alzheimer\'s', 'Stroke', 'High Blood Pressure', 'None'];
const GOAL_OPTIONS = ['Lose Weight', 'Build Muscle', 'Better Sleep', 'Reduce Stress', 'Lower Blood Pressure', 'Increase Energy', 'Live Longer', 'Improve Mental Health'];

export default function SurveyPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [form, setForm] = useState({
    age: 35, weight: 160, height: 68, gender: 'prefer_not_to_say',
    exercise_frequency: 3, exercise_intensity: 'moderate',
    sleep_hours: 7, sleep_quality: 'good',
    diet_type: 'mixed', alcohol_frequency: 'occasionally', smoking: 'never',
    stress_level: 5, chronic_conditions: [], medications: [],
    family_history: [], health_goals: [],
  });

  useEffect(() => {
    // Check if survey already done
    fetchSurvey().then(data => {
      if (data.survey_completed) {
        setCompleted(true);
        if (data.survey_data) setForm(prev => ({ ...prev, ...data.survey_data }));
      }
    }).catch(() => {});
  }, []);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const toggleList = (key, val) => {
    setForm(prev => {
      const list = prev[key] || [];
      return { ...prev, [key]: list.includes(val) ? list.filter(v => v !== val) : [...list, val] };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await submitSurvey(form);
      setUser(result);
      setCompleted(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to submit survey');
    }
    setSubmitting(false);
  };

  if (completed) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: COLORS.background, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div data-testid="survey-complete" style={{ textAlign: 'center', maxWidth: 480 }}>
          <div style={{ width: 80, height: 80, borderRadius: 40, backgroundColor: `${COLORS.success}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <CheckCircle2 size={40} color={COLORS.success} />
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 8 }}>Survey Complete!</h2>
          <p style={{ fontSize: 16, color: COLORS.textSecondary, lineHeight: 1.6 }}>Your health profile has been created. We're generating your personalized health scores now.</p>
          <button onClick={() => navigate('/dashboard')} style={{ marginTop: 24, padding: '14px 28px', borderRadius: 9999, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  const inputStyle = { width: '100%', padding: '12px 16px', borderRadius: 12, border: `1px solid ${COLORS.border}`, fontSize: 15, color: COLORS.textPrimary, backgroundColor: COLORS.surface };
  const labelStyle = { fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, display: 'block', marginBottom: 6 };

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label style={labelStyle}>Age</label>
            <input data-testid="survey-age" type="number" value={form.age} onChange={e => set('age', parseInt(e.target.value) || 0)} style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>Weight (lbs)</label>
              <input data-testid="survey-weight" type="number" value={form.weight} onChange={e => set('weight', parseFloat(e.target.value) || 0)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Height (inches)</label>
              <input data-testid="survey-height" type="number" value={form.height} onChange={e => set('height', parseFloat(e.target.value) || 0)} style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Gender</label>
            <select data-testid="survey-gender" value={form.gender} onChange={e => set('gender', e.target.value)} style={inputStyle}>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non_binary">Non-binary</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </div>
        </div>
      );
      case 1: return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label style={labelStyle}>Exercise Frequency (days per week)</label>
            <input data-testid="survey-exercise-freq" type="range" min="0" max="7" value={form.exercise_frequency} onChange={e => set('exercise_frequency', parseInt(e.target.value))} style={{ width: '100%', accentColor: COLORS.primary }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: COLORS.textSecondary }}><span>0 days</span><span style={{ fontWeight: 700, color: COLORS.primary }}>{form.exercise_frequency} days</span><span>7 days</span></div>
          </div>
          <div>
            <label style={labelStyle}>Exercise Intensity</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {['low', 'moderate', 'high'].map(v => (
                <button key={v} data-testid={`survey-intensity-${v}`} onClick={() => set('exercise_intensity', v)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: `2px solid ${form.exercise_intensity === v ? COLORS.primary : COLORS.border}`, backgroundColor: form.exercise_intensity === v ? `${COLORS.primary}12` : COLORS.surface, color: form.exercise_intensity === v ? COLORS.primary : COLORS.textSecondary, fontWeight: 600, fontSize: 14, cursor: 'pointer', textTransform: 'capitalize' }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
      case 2: return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label style={labelStyle}>Average Sleep (hours per night)</label>
            <input data-testid="survey-sleep" type="range" min="3" max="12" step="0.5" value={form.sleep_hours} onChange={e => set('sleep_hours', parseFloat(e.target.value))} style={{ width: '100%', accentColor: COLORS.primary }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: COLORS.textSecondary }}><span>3 hrs</span><span style={{ fontWeight: 700, color: COLORS.primary }}>{form.sleep_hours} hrs</span><span>12 hrs</span></div>
          </div>
          <div>
            <label style={labelStyle}>Sleep Quality</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {['poor', 'fair', 'good', 'excellent'].map(v => (
                <button key={v} data-testid={`survey-sleep-quality-${v}`} onClick={() => set('sleep_quality', v)} style={{ padding: '12px', borderRadius: 10, border: `2px solid ${form.sleep_quality === v ? COLORS.primary : COLORS.border}`, backgroundColor: form.sleep_quality === v ? `${COLORS.primary}12` : COLORS.surface, color: form.sleep_quality === v ? COLORS.primary : COLORS.textSecondary, fontWeight: 600, fontSize: 14, cursor: 'pointer', textTransform: 'capitalize' }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
      case 3: return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label style={labelStyle}>Diet Type</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {['mixed', 'vegetarian', 'vegan', 'keto', 'paleo', 'mediterranean'].map(v => (
                <button key={v} data-testid={`survey-diet-${v}`} onClick={() => set('diet_type', v)} style={{ padding: '12px', borderRadius: 10, border: `2px solid ${form.diet_type === v ? COLORS.primary : COLORS.border}`, backgroundColor: form.diet_type === v ? `${COLORS.primary}12` : COLORS.surface, color: form.diet_type === v ? COLORS.primary : COLORS.textSecondary, fontWeight: 600, fontSize: 14, cursor: 'pointer', textTransform: 'capitalize' }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Alcohol Consumption</label>
            <select data-testid="survey-alcohol" value={form.alcohol_frequency} onChange={e => set('alcohol_frequency', e.target.value)} style={inputStyle}>
              <option value="never">Never</option>
              <option value="occasionally">Occasionally</option>
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Smoking Status</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {['never', 'former', 'current'].map(v => (
                <button key={v} data-testid={`survey-smoking-${v}`} onClick={() => set('smoking', v)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: `2px solid ${form.smoking === v ? COLORS.primary : COLORS.border}`, backgroundColor: form.smoking === v ? `${COLORS.primary}12` : COLORS.surface, color: form.smoking === v ? COLORS.primary : COLORS.textSecondary, fontWeight: 600, fontSize: 14, cursor: 'pointer', textTransform: 'capitalize' }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
      case 4: return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label style={labelStyle}>Stress Level (1 = very low, 10 = very high)</label>
            <input data-testid="survey-stress" type="range" min="1" max="10" value={form.stress_level} onChange={e => set('stress_level', parseInt(e.target.value))} style={{ width: '100%', accentColor: COLORS.primary }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: COLORS.textSecondary }}><span>Very Low</span><span style={{ fontWeight: 700, color: COLORS.primary }}>{form.stress_level}/10</span><span>Very High</span></div>
          </div>
          <div>
            <label style={labelStyle}>Health Goals (select all that apply)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {GOAL_OPTIONS.map(g => (
                <button key={g} onClick={() => toggleList('health_goals', g)} style={{ padding: '10px 14px', borderRadius: 10, border: `2px solid ${form.health_goals.includes(g) ? COLORS.primary : COLORS.border}`, backgroundColor: form.health_goals.includes(g) ? `${COLORS.primary}12` : COLORS.surface, color: form.health_goals.includes(g) ? COLORS.primary : COLORS.textSecondary, fontWeight: 600, fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
                  {form.health_goals.includes(g) && <CheckCircle2 size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />}{g}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
      case 5: return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label style={labelStyle}>Chronic Conditions (select all that apply)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {CHRONIC_OPTIONS.map(c => (
                <button key={c} onClick={() => toggleList('chronic_conditions', c)} style={{ padding: '10px 14px', borderRadius: 10, border: `2px solid ${form.chronic_conditions.includes(c) ? COLORS.primary : COLORS.border}`, backgroundColor: form.chronic_conditions.includes(c) ? `${COLORS.primary}12` : COLORS.surface, color: form.chronic_conditions.includes(c) ? COLORS.primary : COLORS.textSecondary, fontWeight: 600, fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
                  {form.chronic_conditions.includes(c) && <CheckCircle2 size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />}{c}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Family History (select all that apply)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {FAMILY_OPTIONS.map(f => (
                <button key={f} onClick={() => toggleList('family_history', f)} style={{ padding: '10px 14px', borderRadius: 10, border: `2px solid ${form.family_history.includes(f) ? COLORS.primary : COLORS.border}`, backgroundColor: form.family_history.includes(f) ? `${COLORS.primary}12` : COLORS.surface, color: form.family_history.includes(f) ? COLORS.primary : COLORS.textSecondary, fontWeight: 600, fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
                  {form.family_history.includes(f) && <CheckCircle2 size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />}{f}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
      default: return null;
    }
  };

  const StepIcon = STEPS[step].icon;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: COLORS.background, padding: '32px 20px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
            <ClipboardList size={28} color={COLORS.primary} />
            <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Health Survey</h1>
          </div>
          <p style={{ fontSize: 15, color: COLORS.textSecondary }}>Complete your health assessment to get personalized scores</p>
        </div>

        {/* Progress */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 28 }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: i <= step ? COLORS.primary : COLORS.border, transition: 'background-color 0.3s' }} />
          ))}
        </div>

        {/* Step Card */}
        <div data-testid="survey-form" style={{ backgroundColor: COLORS.surface, borderRadius: 20, padding: 28, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, backgroundColor: `${COLORS.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <StepIcon size={24} color={COLORS.primary} />
            </div>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{STEPS[step].title}</h2>
              <p style={{ fontSize: 13, color: COLORS.textSecondary }}>{STEPS[step].description}</p>
            </div>
            <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: COLORS.textSecondary }}>{step + 1}/{STEPS.length}</span>
          </div>

          {renderStep()}

          {/* Navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, gap: 12 }}>
            <button
              data-testid="survey-prev"
              onClick={() => setStep(s => s - 1)}
              disabled={step === 0}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 20px', borderRadius: 10, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface, color: COLORS.textSecondary, fontWeight: 600, fontSize: 14, cursor: step === 0 ? 'not-allowed' : 'pointer', opacity: step === 0 ? 0.4 : 1 }}
            >
              <ArrowLeft size={18} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                data-testid="survey-next"
                onClick={() => setStep(s => s + 1)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 24px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >
                Next <ArrowRight size={18} />
              </button>
            ) : (
              <button
                data-testid="survey-submit"
                onClick={handleSubmit}
                disabled={submitting}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 24px', borderRadius: 10, border: 'none', backgroundColor: COLORS.success, color: '#FFF', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}
              >
                {submitting ? 'Submitting...' : 'Submit Survey'} <CheckCircle2 size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
